// @ts-nocheck
// Minhas Leituras — lista cronológica de todas as leituras registradas pelo
// user, com ações de editar (páginas + data) e excluir.
//
// Cada edição/delete chama o backend que dispara syncBookProgressFromHistory
// → recalcula current_page do livro em cascata e atualiza tudo (status do
// livro, streak via /api/stats/streak na próxima request, achievements idem).
//
// Acessada via Profile → "Minhas leituras".

import React, { useEffect, useState, useMemo } from 'react';
import { Icon } from './icons';
import { api } from '../services/apiV2';
import { toast } from './toast';

type Entry = {
  id: string;
  book_id: string;
  book_title: string;
  pages_read: number;
  new_page: number;
  reading_date: string;
  daily_goal: number;
  reading_time_minutes?: number;
  completed?: boolean;
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
function isoDay(iso: string): string {
  return iso.slice(0, 10);
}

export const MyReadingsScreen: React.FC<{
  session: any;
  onBack: () => void;
  onChange?: () => void; // dispara reload de stats/lib no caller
}> = ({ session, onBack, onChange }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Entry | null>(null);
  const [filterBook, setFilterBook] = useState<string>('all');

  const load = async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.getReadingHistory(session);
      setEntries(Array.isArray(r) ? r : []);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar leituras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [session]);

  // Lista única de livros pra dropdown de filtro
  const books = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entries) map.set(e.book_id, e.book_title);
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [entries]);

  const filtered = useMemo(() => {
    if (filterBook === 'all') return entries;
    return entries.filter(e => e.book_id === filterBook);
  }, [entries, filterBook]);

  // Agrupa por dia pra exibir mais legível
  const grouped = useMemo(() => {
    const acc: Record<string, Entry[]> = {};
    for (const e of filtered) {
      const day = isoDay(e.reading_date);
      if (!acc[day]) acc[day] = [];
      acc[day].push(e);
    }
    return Object.entries(acc).sort((a, b) => b[0].localeCompare(a[0])); // desc
  }, [filtered]);

  const totalPages = filtered.reduce((s, e) => s + (e.pages_read || 0), 0);

  return (
    <div className="scroll" style={{ paddingBottom: 100 }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="chevron-left" size={16}/>
        </button>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ember)', fontWeight: 600, marginTop: 24,
        }}>Histórico</div>
        <div className="serif" style={{
          fontSize: 32, lineHeight: 1.05, marginTop: 6, letterSpacing: '-0.01em',
        }}>
          Minhas <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>leituras</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
          {entries.length} {entries.length === 1 ? 'registro' : 'registros'} · {totalPages} páginas no total.
          Toque em uma leitura pra editar ou excluir.
        </div>
      </div>

      {/* Filtro por livro */}
      {books.length > 1 && (
        <div style={{ padding: '20px 20px 0' }}>
          <select
            value={filterBook}
            onChange={(e) => setFilterBook(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 12,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              color: 'var(--ink)', fontSize: 13, fontFamily: 'inherit',
            }}
          >
            <option value="all">Todos os livros ({entries.length})</option>
            {books.map(b => (
              <option key={b.id} value={b.id}>{b.title}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div style={{
          margin: '20px 20px 0', padding: 14, borderRadius: 12,
          background: 'rgba(192,58,42,0.08)', border: '1px solid rgba(192,58,42,0.3)',
          color: '#C03A2A', fontSize: 13,
        }}>{error}</div>
      )}

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Carregando…
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <div style={{
          margin: '32px 20px 0', padding: 32, textAlign: 'center',
          background: 'var(--paper-2)', borderRadius: 16, border: '1px dashed var(--line)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📖</div>
          <div className="serif" style={{ fontSize: 18, color: 'var(--ink)', marginBottom: 6 }}>
            Sem leituras ainda
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', maxWidth: 340, margin: '0 auto', lineHeight: 1.5 }}>
            Quando você registrar sua primeira sessão de leitura, ela aparece aqui.
            Você poderá editar ou excluir cada registro a qualquer momento.
          </div>
        </div>
      )}

      {/* Lista agrupada por dia */}
      <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {grouped.map(([day, dayEntries]) => {
          const dayTotal = dayEntries.reduce((s, e) => s + (e.pages_read || 0), 0);
          return (
            <div key={day}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div className="mono" style={{
                  fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: 'var(--ember)', fontWeight: 600,
                }}>{fmtDate(dayEntries[0].reading_date)}</div>
                <div className="mono" style={{
                  fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: '0.06em',
                }}>{dayTotal} páginas</div>
              </div>
              <div style={{
                background: 'var(--paper-2)', borderRadius: 14,
                border: '1px solid var(--line)', overflow: 'hidden',
              }}>
                {dayEntries.map((e, i) => (
                  <button
                    key={e.id}
                    onClick={() => setEditing(e)}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '12px 14px',
                      borderTop: i > 0 ? '1px solid var(--line)' : 'none',
                      background: 'transparent', border: 'none',
                      display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: 'var(--ember-soft)', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 600, color: 'var(--ember)',
                    }}>
                      {e.pages_read}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, color: 'var(--ink)', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{e.book_title}</div>
                      <div className="mono" style={{
                        fontSize: 10.5, color: 'var(--ink-3)', letterSpacing: '0.04em',
                        marginTop: 3,
                      }}>
                        {e.pages_read} {e.pages_read === 1 ? 'página' : 'páginas'} · até pg {e.new_page}
                        {e.reading_time_minutes ? ` · ${e.reading_time_minutes} min` : ''}
                        {' · '}{fmtTime(e.reading_date)}
                      </div>
                    </div>
                    <Icon name="chevron-right" size={14} color="var(--ink-4)"/>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sheet de edição */}
      {editing && (
        <EditEntrySheet
          entry={editing}
          session={session}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
            onChange?.();
            toast.success('Registro atualizado');
          }}
          onDeleted={async () => {
            setEditing(null);
            await load();
            onChange?.();
            toast.success('Registro excluído');
          }}
        />
      )}
    </div>
  );
};

const EditEntrySheet: React.FC<{
  entry: Entry;
  session: any;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}> = ({ entry, session, onClose, onSaved, onDeleted }) => {
  const [pages, setPages] = useState(String(entry.pages_read));
  const [date, setDate] = useState(isoDay(entry.reading_date));
  const [busy, setBusy] = useState<'saving' | 'deleting' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const n = parseInt(pages, 10);
    if (!Number.isFinite(n) || n < 0) {
      setError('Páginas inválido (use número >= 0).');
      return;
    }
    if (!date) {
      setError('Data inválida.');
      return;
    }
    setBusy('saving');
    setError(null);
    try {
      await api.updateReadingEntry(session, entry.id, {
        pages_read: n,
        reading_date: date,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar');
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir este registro de leitura?\n\nIsso vai recalcular automaticamente o progresso do livro e seus stats.')) return;
    setBusy('deleting');
    setError(null);
    try {
      await api.deleteReadingEntry(session, entry.id);
      onDeleted();
    } catch (e: any) {
      setError(e?.message || 'Erro ao excluir');
      setBusy(null);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 90,
        background: 'rgba(20,16,14,0.55)', backdropFilter: 'blur(4px)',
      }}/>
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 91,
        background: 'var(--paper)', borderTopLeftRadius: 24, borderTopRightRadius: 24,
        padding: '14px 20px 32px', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 -16px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          width: 40, height: 4, borderRadius: 2, background: 'var(--line)',
          margin: '0 auto 16px',
        }}/>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>
            Editar <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>leitura</span>
          </div>
          <button onClick={onClose} disabled={!!busy} style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="close" size={14}/>
          </button>
        </div>

        <div style={{
          marginBottom: 16, padding: 12, borderRadius: 10,
          background: 'var(--paper-2)', border: '1px solid var(--line)',
          fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5,
        }}>
          <strong style={{ color: 'var(--ink-2)' }}>{entry.book_title}</strong>
          <br/>
          Original: <strong>{entry.pages_read}</strong> páginas em <strong>{fmtDate(entry.reading_date)}</strong>
        </div>

        {error && (
          <div style={{
            padding: 12, borderRadius: 10, marginBottom: 14,
            background: 'rgba(192,58,42,0.08)', border: '1px solid rgba(192,58,42,0.3)',
            color: '#C03A2A', fontSize: 13,
          }}>{error}</div>
        )}

        <Field label="Páginas lidas">
          <input
            type="number" min={0} step={1}
            value={pages}
            onChange={(e) => setPages(e.target.value)}
            disabled={!!busy}
            style={inputStyle}
          />
        </Field>

        <Field label="Data">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={!!busy}
            style={inputStyle}
          />
        </Field>

        <button
          onClick={handleSave}
          disabled={!!busy}
          style={{
            width: '100%', marginTop: 18, padding: '14px 18px',
            background: busy === 'saving' ? 'var(--paper-2)' : 'var(--ember)',
            color: busy === 'saving' ? 'var(--ink-3)' : 'var(--paper)',
            border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 600,
            cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit',
            boxShadow: busy ? 'none' : '0 12px 24px -10px rgba(199,83,26,0.5)',
          }}
        >
          {busy === 'saving' ? 'Salvando…' : 'Salvar alterações'}
        </button>

        <button
          onClick={handleDelete}
          disabled={!!busy}
          style={{
            width: '100%', marginTop: 10, padding: '13px 18px',
            background: 'transparent', color: '#C03A2A',
            border: '1px solid rgba(192,58,42,0.4)', borderRadius: 14,
            fontSize: 13, fontWeight: 500, cursor: busy ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {busy === 'deleting' ? 'Excluindo…' : 'Excluir registro'}
        </button>

        <div className="mono" style={{
          marginTop: 14, fontSize: 9.5, color: 'var(--ink-4)',
          letterSpacing: '0.06em', textAlign: 'center',
        }}>
          ✦ Toda alteração recalcula automaticamente o progresso do livro e seus stats.
        </div>
      </div>
    </>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', borderRadius: 10,
  background: 'var(--paper-2)', border: '1px solid var(--line)',
  color: 'var(--ink)', fontSize: 14, outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="mono" style={{
      display: 'block',
      fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: 'var(--ink-3)', fontWeight: 600, marginBottom: 6,
    }}>{label}</label>
    {children}
  </div>
);
